import { AppControlData, SignatureData } from "@/api/signatures";
import { TFunction } from "i18next";
import { RuleTargetType } from "@/api/rulesLibrary";
import { useSignatureStore } from "@/stores/v2/signatureStore";
import { parseDomain } from "@/utils/urlUtils";
import { EXCLUDED_SIGNATURE_IDS } from "../constants";

/** Dropdown rows: API rule types, in-class app controls, plus `section` for picker headings. */
export type RulePickerTargetType = RuleTargetType | "APP_CONTROL";
export type RuleTargetOptionType = "section" | RulePickerTargetType;

export interface RuleTargetOption {
	type: RuleTargetOptionType;
	value: string;
	label: string;
	favicon?: string;
	platforms?: AppControlData["platforms"];
}

/** Parses a free-text URL/domain input into a hostname-only website target. */
export function transformWebsiteFilter(value: string): string {
	const re = /^(http:\/\/|https:\/\/|)([a-z0-9A-Z.-]+\.?)\.([a-z0-9A-Z.-]+)(\/|)(.*)/i;
	if (!re.test(value)) {
		return "";
	}

	const match = re.exec(value);
	if (!match) {
		return "";
	}

	let host = "";
	if (match[2] !== "") {
		host += match[2];
	}
	if (match[3] !== "") {
		host += "." + match[3];
	}

	return host;
}

/** Hostname used to look up a pasted website against {@link useSignatureStore.findSignatureByUrl}. */
function websiteLookupDomain(website: string): string | undefined {
	const domain =
		parseDomain(website) ??
		transformWebsiteFilter(website)
			.replace(/^www\./i, "")
			.toLowerCase();
	return domain || undefined;
}

/** When a pasted URL maps to a known signature, use that signature instead of a website rule. */
export function findSignatureForWebsite(website: string): SignatureData | undefined {
	const domain = websiteLookupDomain(website);
	if (!domain) {
		return undefined;
	}

	return useSignatureStore.getState().findSignatureByUrl(domain);
}

export function resolveRuleTarget(rawTarget: string): { target: string; type: RuleTargetType } {
	const trimmed = rawTarget.trim();
	const byId = useSignatureStore.getState().findSignature(trimmed);
	if (byId) {
		return { target: byId.id, type: "SIGNATURE" };
	}

	const byWebsite = findSignatureForWebsite(trimmed);
	if (byWebsite && !EXCLUDED_SIGNATURE_IDS.has(byWebsite.id)) {
		return { target: byWebsite.id, type: "SIGNATURE" };
	}

	const websiteTarget = parseDomain(trimmed) ?? transformWebsiteFilter(trimmed);
	return {
		target: websiteTarget || trimmed,
		type: "URL",
	};
}

export function getEnabledSignatures(signatures: SignatureData[]): SignatureData[] {
	return signatures.filter((signature) => signature.enabled && !EXCLUDED_SIGNATURE_IDS.has(signature.id));
}

function matchesFilter(label: string, filter: string): boolean {
	const labelLower = label.toLowerCase();
	const filterLower = filter.toLowerCase();

	if (filterLower.length === 0) {
		return true;
	}
	if (filterLower.length <= 2) {
		return labelLower.startsWith(filterLower);
	}
	return labelLower.includes(filterLower);
}

/**
 * Builds the selectable options for the rule-target picker, including section headers
 * and a dynamic website entry when the filter looks like a valid URL.
 */
export function buildRuleTargetOptions(params: {
	signatures: SignatureData[];
	filter: string;
	t: TFunction;
	maxResults?: number;
	appControls?: AppControlData[];
	includeAppControls?: boolean;
}): RuleTargetOption[] {
	const { signatures, filter, t, maxResults = 100, appControls = [], includeAppControls = false } = params;
	const options: RuleTargetOption[] = [];

	const matchingSignatures = getEnabledSignatures(signatures).filter((signature) => matchesFilter(signature.name, filter));
	if (matchingSignatures.length > 0) {
		options.push({ type: "section", value: "section-signatures", label: t("policyDrawer.sectionSignaturesAndCategories") });
		for (const signature of matchingSignatures) {
			options.push({
				type: "SIGNATURE",
				value: signature.id,
				label: signature.name,
				favicon: signature.favicon_url ?? undefined,
			});
		}
	}

	if (includeAppControls) {
		const matchingAppControls = appControls.filter((appControl) => matchesFilter(appControl.name, filter));
		if (matchingAppControls.length > 0) {
			options.push({ type: "section", value: "section-app-controls", label: t("policyDrawer.sectionAppControls") });
			for (const appControl of matchingAppControls) {
				options.push({
					type: "APP_CONTROL",
					value: appControl.id,
					label: appControl.name,
					favicon: appControl.favicon || undefined,
					platforms: appControl.platforms,
				});
			}
		}
	}

	const websiteTarget = transformWebsiteFilter(filter);
	if (websiteTarget !== "") {
		const matchedSignature = findSignatureForWebsite(filter);
		if (matchedSignature && !EXCLUDED_SIGNATURE_IDS.has(matchedSignature.id)) {
			const alreadyListed = options.some((option) => option.type === "SIGNATURE" && option.value === matchedSignature.id);
			if (!alreadyListed) {
				const hasSignaturesSection = options.some((option) => option.type === "section" && option.value === "section-signatures");
				if (!hasSignaturesSection) {
					options.push({ type: "section", value: "section-signatures", label: t("policyDrawer.sectionSignaturesAndCategories") });
				}
				options.push({
					type: "SIGNATURE",
					value: matchedSignature.id,
					label: matchedSignature.name,
					favicon: matchedSignature.favicon_url ?? undefined,
				});
			}
		} else {
			options.push({
				type: "URL",
				value: websiteTarget,
				label: t("createPolicy.websiteOptionPrefix", { value: websiteTarget }),
			});
		}
	}

	return options.slice(0, maxResults);
}
